import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { match, P } from "ts-pattern";
import { Page } from "../../../activities/AppPage";
import { API } from "../../../API";
import useCaregiver from "../../../hooks/useCaregiver";
import useDisablePushNotifications from "../../../hooks/useDisablePushNotifications";
import useMixin from "../../../hooks/useMixin";
import { useSocket } from "../../../hooks/useSocket";
import { isLoading } from "../../../Loading";
import {
  AgencyId,
  CommCenterMessageActionId,
  CommCenterMessageId,
  CommCenterTicketId,
} from "../../../messages";
import {
  CaregiverCommCenterMessage,
  CommCenterCaregiverTicketList,
  CommCenterGetTicketResponse,
} from "../../../messages/CommunicationCenter";
import { STRINGS } from "../../../strings";
import { AppState } from "../../../system/AppState";
import { translate } from "../../../translate";
import { flmap } from "../../../utils/fmap";
import { getImagesDetails } from "../../../utils/imageUtils";
import { QueryStatus } from "../../../utils/reactQueryUtils";
import ErrorScaffold from "../../ErrorScaffold";
import { withMedflytAgencyId } from "../../withMedflytAgencyId";
import { chatTicketsQueryKey } from "../ChatMainScreen/ChatMainActivity";
import {
  CommCenterListActionItem,
  getChatRoomOptimisticUpdateData,
  getTicketListWithAllMessagesRead,
  isChatMessageUnread,
} from "../utils/chatUtils";
import { chatRoomQueryKey } from "./chatRoomQueryKey";
import ChatRoomScreen from "./ChatRoomScreen";

interface Props {
  api: API;
  appState: AppState;
  medflytAgencyId: AgencyId;
  ticketId: CommCenterTicketId;
  onRequestNavigate: (page: Page) => void;
  onRequestNavigateReplace: (page: Page) => void;
  onClickGoBack: () => void;
}

function ChatRoomActivity(props: Props) {
  const ticketId = props.ticketId;

  const caregiver = useCaregiver(props.appState);

  const [sendActionStatus, setSendActionStatus] = React.useState<"sending" | "sent" | null>(null);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const { appVisitsState, scheduledVisits } = useMixin(props.appState);

  const visitBroadbasts = flmap(appVisitsState, (state) => state.visits);

  const visitInstances = flmap(scheduledVisits, (state) => state.scheduledVisitDetails);

  const handleScrollDown = () => {
    const current = containerRef.current;

    if (current !== null) {
      current.scrollTop = current.scrollHeight;
    }
  };

  const query = useQuery({
    queryKey: chatRoomQueryKey(ticketId),
    queryFn: () => props.api.getChatTicket(ticketId),
    onSuccess: (data) => {
      setTimeout(() => handleScrollDown());

      if (sendActionStatus === "sending") {
        setSendActionStatus(null);
      }

      const lastMessage = data.ticket.messages.at(-1);

      if (lastMessage !== undefined && isChatMessageUnread(lastMessage)) {
        markMessageAsReadMutation.mutate(lastMessage.id);
        queryClient.setQueryData<CommCenterCaregiverTicketList | undefined>(
          chatTicketsQueryKey,
          getTicketListWithAllMessagesRead
        );
      }
    },
  });

  const markMessageAsReadMutation = useMutation({
    mutationFn: (messageId: CommCenterMessageId) =>
      props.api.markMessageAsRead(ticketId, { latestMessageId: messageId }),
  });

  const handleUploadPhotos = async (images: string[]) => {
    if (isLoading(caregiver) || images.length === 0) {
      return [];
    }

    const blobs = await getImagesDetails(images);

    const s3Urls = await props.api.uploadPhotos(
      caregiver.id,
      blobs.map((x) => x.blob)
    );

    return s3Urls.photos;
  };

  const handleUploadDocuments = async (documents: string[]) => {
    if (isLoading(caregiver) || documents.length === 0) {
      return [];
    }

    const blobs = await Promise.all(
      documents.map((url) => fetch(url).then((response) => response.blob()))
    );

    const result = await props.api.uploadDocuments(caregiver.id, blobs);

    const s3Keys = result.documents;

    return s3Keys;
  };

  const handleUploadVideos = async (videos: string[]) => {
    if (isLoading(caregiver) || videos.length === 0) {
      return [];
    }

    const blobs = await Promise.all(
      videos.map((url) => fetch(url).then((response) => response.blob()))
    );

    const result = await props.api.uploadVideos(caregiver.id, blobs);

    const s3Keys = result.videos;

    return s3Keys;
  };

  const sendChatMessageMutation = useMutation({
    mutationFn: async (params: {
      ticketId: CommCenterTicketId;
      message: CaregiverCommCenterMessage;
    }) => {
      const photoUrls = await handleUploadPhotos(
        params.message.flatMap((message) => (message.type === "IMAGE" ? [message.url] : []))
      );
      const documentUrls = await handleUploadDocuments(
        params.message.flatMap((message) => (message.type === "DOCUMENT" ? [message.url] : []))
      );

      const videoUrls = await handleUploadVideos(
        params.message.flatMap((message) => (message.type === "VIDEO" ? [message.url] : []))
      );

      props.api.sendChatMessage(params.ticketId, {
        message: [
          ...params.message.filter(
            (m) => m.type !== "IMAGE" && m.type !== "DOCUMENT" && m.type !== "VIDEO"
          ),
          ...photoUrls.map((url) => ({
            type: "IMAGE" as const,
            url,
          })),
          ...documentUrls.map((url) => ({
            type: "DOCUMENT" as const,
            url,
          })),
          ...videoUrls.map((url) => ({
            type: "VIDEO" as const,
            url,
          })),
        ],
      });
    },
    onMutate: async (params) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(chatRoomQueryKey(ticketId));

      // Snapshot the previous value
      const previousTicket = queryClient.getQueryData<CommCenterGetTicketResponse>(
        chatRoomQueryKey(ticketId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CommCenterGetTicketResponse | undefined>(
        chatRoomQueryKey(ticketId),
        (old) => {
          if (old === undefined || isLoading(caregiver)) {
            return old;
          }

          if (params.message.some((m) => m.type === "ACTION")) {
            setSendActionStatus("sending");
          }

          setTimeout(() => handleScrollDown());

          return getChatRoomOptimisticUpdateData({
            old: old,
            ticketId: ticketId,
            caregiver: caregiver,
            message: params.message,
          });
        }
      );

      // Return a context object with the snapshotted value
      return { previousTicket };
    },
    onError: (
      _,
      __,
      context:
        | {
            previousTicket: CommCenterGetTicketResponse | undefined;
          }
        | undefined
    ) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      queryClient.setQueryData(chatRoomQueryKey(ticketId), context?.previousTicket);

      queryClient.invalidateQueries(chatRoomQueryKey(ticketId));
    },
  });

  const handleClickActionItem = (
    item: CommCenterListActionItem,
    actionId: CommCenterMessageActionId
  ) => {
    if (sendChatMessageMutation.isLoading) {
      return;
    }

    sendChatMessageMutation.mutate({
      ticketId: ticketId,
      message: [
        {
          type: "ACTION",
          actionId: actionId,
          entitiyType: item.entity,
          entitiyId: item.id,
          entitiyText: item.text,
          entitiyIcon: item.icon,
        },
      ],
    });
  };

  const handleClickGoBack = () => {
    imagePreview !== null ? setImagePreview(null) : props.onClickGoBack();
  };

  useSocket({
    eventName: "CommCenterTicketUpdated",
    onEvent: (payload) => {
      if (payload.ticketId === ticketId) {
        queryClient.invalidateQueries(chatRoomQueryKey(ticketId));
      }
    },
  });

  useSocket({
    eventName: "CommCenterRedirect",
    onEvent: props.onRequestNavigate,
  });

  useSocket({
    eventName: "CommCenterChatRoomRedirect",
    onEvent: (payload) => {
      queryClient.invalidateQueries(chatRoomQueryKey(payload.ticketId));
      props.onRequestNavigateReplace(payload);
    },
  });

  const [images, setImages] = React.useState<string[]>([]);

  const [documents, setDocuments] = React.useState<string[]>([]);
  const [videos, setVideos] = React.useState<string[]>([]);

  const [imagePreview, setImagePreview] = React.useState<string | null>(null);

  const handleAddImages = (newImages: string[]) => {
    setImages([...images, ...newImages]);
  };

  const handleAddDocuments = (newDocuments: string[]) => {
    setDocuments([...documents, ...newDocuments]);
  };

  const handleRemoveDocument = (document: string) => {
    setDocuments(documents.filter((i) => i !== document));
  };

  const handleAddVideos = (newVideos: string[]) => {
    setVideos([...videos, ...newVideos]);
  };

  const handleRemoveVideo = (video: string) => {
    setVideos(videos.filter((i) => i !== video));
  };

  const handleRemoveImage = (image: string) => {
    setImages(images.filter((i) => i !== image));
  };

  const handleRemoveAllImages = () => {
    setImages([]);
  };

  const handleRemoveAllDocuments = () => {
    setDocuments([]);
  };

  const handleRemoveAllVideos = () => {
    setVideos([]);
  };

  useDisablePushNotifications({
    name: "ChatRoomActivity",
    disableIf: (notification) => {
      return (
        notification?.type === "COMM_CENTER_TICKET_MESSAGE" && notification.ticketId === ticketId
      );
    },
  });

  const getEagerLoadedTicket = () => {
    const tickets = queryClient.getQueryData<CommCenterCaregiverTicketList>(chatTicketsQueryKey);

    return (
      tickets?.tickets.find((t) => t.id === ticketId) ??
      queryClient.getQueryData<CommCenterGetTicketResponse>(chatRoomQueryKey(ticketId))?.ticket
    );
  };

  const typingPerson = React.useMemo(() => {
    const lastMessage = query.data?.ticket.messages.at(-1);

    if (sendActionStatus !== null) {
      return {
        name: "Medflyt",
        avatarUrl: null,
      };
    }

    if (
      lastMessage?.createdBy.type === "Caregiver" &&
      query.data?.ticket.enableAgencyResponse === false
    ) {
      return {
        name: "Medflyt",
        avatarUrl: null,
      };
    }

    return null;
  }, [query.data?.ticket.enableAgencyResponse, query.data?.ticket.messages, sendActionStatus]);

  return match(query)
    .with(QueryStatus.Error, () => (
      <ErrorScaffold
        title={translate(STRINGS.CHAT_WITH_MEDFLYT)}
        message={translate(STRINGS.SOMETHING_WENT_WRONG)}
        onClickRetry={query.refetch}
        onClickGoBack={props.onClickGoBack}
      />
    ))
    .with(P.union(QueryStatus.Loading, QueryStatus.Idle), () => {
      const eagerLoadedTicket = getEagerLoadedTicket();

      if (eagerLoadedTicket !== undefined) {
        return (
          <ChatRoomScreen
            isLoading={false}
            visitBroadcasts={visitBroadbasts}
            visitInstances={visitInstances}
            ticket={eagerLoadedTicket}
            typingPerson={typingPerson}
            images={images}
            documents={documents}
            videos={videos}
            containerRef={containerRef}
            imagePreview={imagePreview}
            onSendMessage={sendChatMessageMutation.mutate}
            onClickActionItem={handleClickActionItem}
            onClickGoBack={handleClickGoBack}
            onSelectImages={handleAddImages}
            onClickRemoveImage={handleRemoveImage}
            onSelectDocuments={handleAddDocuments}
            onClickRemoveDocument={handleRemoveDocument}
            onClickRemoveVideo={handleRemoveVideo}
            onResetVideos={handleRemoveAllVideos}
            onSelectVideos={handleAddVideos}
            onResetImages={handleRemoveAllImages}
            onResetDocuments={handleRemoveAllDocuments}
            onRequestImagePreview={setImagePreview}
            onRequestNavigate={props.onRequestNavigate}
          />
        );
      }

      return (
        <ChatRoomScreen
          isLoading={true}
          visitBroadcasts={visitBroadbasts}
          visitInstances={visitInstances}
          images={images}
          documents={documents}
          typingPerson={typingPerson}
          containerRef={containerRef}
          imagePreview={imagePreview}
          videos={videos}
          onSendMessage={sendChatMessageMutation.mutate}
          onClickActionItem={handleClickActionItem}
          onClickGoBack={handleClickGoBack}
          onSelectImages={handleAddImages}
          onSelectDocuments={handleAddDocuments}
          onClickRemoveDocument={handleRemoveDocument}
          onResetDocuments={handleRemoveAllDocuments}
          onClickRemoveImage={handleRemoveImage}
          onResetImages={handleRemoveAllImages}
          onRequestImagePreview={setImagePreview}
          onResetVideos={handleRemoveAllVideos}
          onSelectVideos={handleAddVideos}
          onClickRemoveVideo={handleRemoveVideo}
          onRequestNavigate={props.onRequestNavigate}
        />
      );
    })
    .with(QueryStatus.ResolvedAs("data"), (result) => (
      <ChatRoomScreen
        isLoading={false}
        visitBroadcasts={visitBroadbasts}
        visitInstances={visitInstances}
        ticket={result.data.ticket}
        typingPerson={typingPerson}
        images={images}
        documents={documents}
        videos={videos}
        containerRef={containerRef}
        imagePreview={imagePreview}
        onSendMessage={sendChatMessageMutation.mutate}
        onClickActionItem={handleClickActionItem}
        onClickGoBack={handleClickGoBack}
        onSelectImages={handleAddImages}
        onClickRemoveImage={handleRemoveImage}
        onClickRemoveDocument={handleRemoveDocument}
        onResetDocuments={handleRemoveAllDocuments}
        onSelectDocuments={handleAddDocuments}
        onResetImages={handleRemoveAllImages}
        onRequestImagePreview={setImagePreview}
        onResetVideos={handleRemoveAllVideos}
        onSelectVideos={handleAddVideos}
        onClickRemoveVideo={handleRemoveVideo}
        onRequestNavigate={props.onRequestNavigate}
      />
    ))
    .exhaustive();
}

export default withMedflytAgencyId(ChatRoomActivity);
